<div [formGroup]="form">
  <div class="page-header">
    <h1>Overview</h1>
  </div>

  <h2>Before you apply</h2>
  <h3>Who may use this application?</h3>

  @if (formType === ApplicationType.Victim_Application) {
    <div>
      <p>This application form is designed for a <strong>Victim</strong> of violent crime. Under the <em>Crime Victim Assistance Act</em>, a victim is a person who is injured, physically or psychologically, as a result of an eligible criminal offence committed in British Columbia.</p>
      <p>If this definition does not apply to you, please see the application forms for <a routerLink="/application/witness">Witnesses</a> or <a routerLink="/application/ifm">Immediate Family Members</a>.</p>
      <p>If the victim is deceased, please see the application for <a routerLink="/application/ifm">Immediate Family Members</a>.</p>
    </div>
  }
  @if (formType === ApplicationType.IFM_Application) {
    <div>
      <p>
        This application package is designed for an <strong>Immediate Family Member</strong> of an injured or deceased victim of violent crime. Under the Crime Victim Assistance Act, an Immediate Family Member is a person who, at the time of the eligible offence that resulted in the death or injury of a victim, was a spouse, child, grandchild, sibling, step sibling, half sibling, parent or grandparent of the victim.
      </p>
      <p>
        Changes to the <i>Crime Victim Assistance Act</i> came into effect on January 1, 2024 and expanded the definition of Immediate Family Member. If you are applying in relation to an offence that happened before January 1, 2024, the definition of Immediate Family Member that was in effect at that time will apply.
        <app-tool-tip [trigger]="changestotheCrimeVictim"></app-tool-tip>
      </p>
      <p>If this definition does not apply to you, please see the application forms for <a routerLink="/application/witness">Witnesses</a> or <a routerLink="/application/victim">Victims</a>.</p>
    </div>
  }
  @if (formType === ApplicationType.Witness_Application) {
    <div>
      <p>This application package is designed for a <strong>Witness</strong> of an injured or deceased victim of violent crime. Under the <em>Crime Victim Assistance Act</em>, a witness is a person who witnesses in close proximity a prescribed, or the immediate aftermath of a prescribed offence, that causes a life-threatening injury to, or the death of, the victim.</p>
      <p>If this definition does not apply to you, please see the application forms for <a routerLink="/application/ifm">Immediate Family Members</a> or <a routerLink="/application/victim">Victims</a>.</p>
      <p>
        Changes to the <i>Crime Victim Assistance Act</i> came into effect on January 1, 2024 and expanded the definition of Witness. If you are applying in relation to an offence that happened before January 1, 2024, the definition of Witness that was in effect at that time will apply.
        <app-tool-tip [trigger]="witnessTooltip"></app-tool-tip>
      </p>
    </div>
  }

  <div class="attention">
    <h3>Note</h3>
    <p>
      To assist those completing more than one application for members of the same family affected by the same crime, certain information (e.g. address, police information) will automatically be entered from the previous application. Please check to ensure the accuracy of the auto-populated information before signing and submitting each application. All pre-populated fields are editable if needed.
    </p>
  </div>

  <h3>What types of benefits does the Crime Victim Assistance Program provide?</h3>

  <p>Benefits that may be available to {{applicant}}@if (formType === ApplicationType.Victim_Application) {
    <span> of violent crime</span>
  } include:</p>
  <ul class="list">
    @if (formType === ApplicationType.Victim_Application) {
      <li>medical and dental expenses</li>
      <li>prescription drug expenses</li>
      <li>counselling</li>
      <li>lost employment income (reimbursed at minimum wage)</li>
      <li>repair or replacement costs of damaged or destroyed property that a person was wearing at the time of the crime (e.g., eyeglasses, clothing)</li>
      <li>protective measures (e.g., moving expenses)</li>
      <li>disability benefits, services or equipment</li>
    }

    @if (formType === ApplicationType.IFM_Application) {
      <li>counselling</li>
      <li>prescription drug expenses</li>
      <li>transportation and related expenses</li>
      <li>funeral expenses</li>
      <li>bereavement leave</li>
      <li>income support</li>
      <li>loss of parental guidance</li>
    }

    @if (formType === ApplicationType.Witness_Application) {
      <li>counselling</li>
      <li>prescription drug expenses</li>
      <li>transportation and related expenses</li>
      <li>crime scene cleaning</li>
    }
  </ul>

  <h3>The Crime Victim Assistance Program will NOT cover:</h3>
  <ul class="list">
    <li>injuries or loss sustained from motor vehicle accidents</li>
    <li>injuries or loss sustained at work, and which are covered by WorkSafeBC</li>
    <li>claims for pain and suffering</li>
    <li>lost or stolen property</li>
    <li>injuries sustained from an offence occurring outside of B.C. or prior to July 1, 1972</li>
  </ul>

  <div class="attention">
    <h3>Note</h3>
    <p>
      The Crime Victim Assistance Program (CVAP) will only provide benefits that are not covered by other programs<br />
      (e.g., EI, CPP, ICBC, extended health coverage, personal insurance).
    </p>
    @if (formType === ApplicationType.Victim_Application) {
      <p>Therefore, if you are applying for benefits (e.g. income support) that may also be covered by another program (e.g. EI), you will need to apply to that other program in order for CVAP to determine your eligibility for the benefit requested. Applying to another program will not prevent you from being able to apply to CVAP as well. Please contact us if you have any questions.</p>
    }
    @if (formType === ApplicationType.IFM_Application) {
      <p>Therefore, if you are applying for benefits (e.g. income support) that may also be covered by another program (e.g. CPP survivor's pension), you will need to apply to that other program in order for CVAP to determine your eligibility for the benefit requested. Applying to another program will not prevent you from being able to apply to CVAP as well. Please contact us if you have any questions.</p>
    }
    @if (formType === ApplicationType.Witness_Application) {
      <p>Therefore, if you are applying for benefits (e.g. prescription drugs) that may also be covered by another program (e.g. extended health coverage), you will need to apply to that other program in order for CVAP to determine your eligibility for the benefit requested. Applying to another program will not prevent you from being able to apply to CVAP as well. Please contact us if you have any questions.</p>
    }
    <p>View full <a [routerLink]="[]" (click)="showSummaryOfBenefits()">summary of benefits</a></p>
  </div>

  <h2>Instructions</h2>

  <h3>Be complete and accurate</h3>
  <p>Please ensure that all form fields in the web application are done accurately, as incorrect information will affect the processing of your claim. <b>Please note that using the navigation buttons (back and forward arrows) in the browser header will exit you from the application form, and the information you have entered will be lost.</b> As such, to move within the web application use the CONTINUE buttons at the bottom of the page and/or click on the page names (e.g. Overview) in the top left corner of the form.</p>

  <h3>What you need to have:</h3>
  <p>In order to complete this form efficiently please make sure you have the following on hand:</p>
  <ul class="list">
    <li>All information pertaining to the incident</li>
    <li>Your own health information (PHN)</li>
    <li>Police Officers contact (if known)</li>
    @if (formType === ApplicationType.Victim_Application || formType === ApplicationType.IFM_Application) {
      <li>Employment information (if requesting benefits for lost employment income)</li>
    }
    <li>Info about the deceased (if it was a fatality)</li>
    <li>What benefits you are claiming (View <a [routerLink]="[]" (click)="showSummaryOfBenefits()">Summary of Benefits</a>)</li>
    <li>Victim Service Worker's information (if available)</li>
    <li>Court File Number (if known)</li>
    <li>Date of crime</li>
    <li>Date reported to police</li>
    @if (formType === ApplicationType.Victim_Application) {
      <li>Date of hospital visit (if applicable)</li>
    }
    @if (formType === ApplicationType.Victim_Application) {
      <li>Name of hospital (if applicable)</li>
    }
    <li>Date of any health care professional (ie., doctor, psychiatrist) visit</li>
    <li>Name of any health care professional (if applicable)</li>
  </ul>



  <p>If your address or telephone number changes after submitting this application, please inform the Crime Victim Assistance Program by email at <a [href]="'mailto: ' + cvapEmail">{{cvapEmail}}</a> or by calling <strong class="phone-number">1-866-660-3888</strong>.</p>
  <p>For additional questions, please contact the Crime Victim Assistance Program by email at <a [href]="'mailto: ' + cvapEmail">{{cvapEmail}}</a> or at <strong>604-660-3888</strong> or toll free at <strong class="phone-number">1-866-660-3888</strong>.</p>
  <p>For more information, see the Government of British Columbia website at <a href="https://www.gov.bc.ca/crimevictimassistance" target="_blank">www.gov.bc.ca/crimevictimassistance</a> or query &ldquo;cvap bc&rdquo; using your internet search engine.</p>
  <br /><br />

  <section>
    <app-field [valid]="isMyControlValid(form.get('understoodInformation'))" errorMessage="Please confirm that you have read and understand the above information">
      <label class="inline-label">
        <input type="checkbox" style="margin-right: 10px;" formControlName="understoodInformation" [value]="true">
        <span>I have read and understood all the above information. <span class="error-states ng-star-inserted">*</span></span>
      </label>
    </app-field>
  </section>
</div>
<ng-template #changestotheCrimeVictim>
  For offences that occurred before January 1, 2024, an Immediate Family Member is defined as a person who, at the time of the eligible offence that resulted in the death or injury of a victim, was a spouse, child, sibling, step sibling,half sibling, parent of the victim or, <strong>if financially dependent on the victim, a grandparent or grandchild of the victim.</strong>
</ng-template>
<ng-template #witnessTooltip>
  For offences that occurred before January 1, 2024, a Witness is defined as a person who is not related to the victim but <strong>has a strong emotional attachment to the victim</strong> and witnesses in close proximity an eligible offence that causes a life-threatening injury or death to the victim <strong>or</strong> witnesses the immediate aftermath of an eligible offence that caused the death of the victim.
</ng-template>
